import * as React from "react";
import styled from "styled-components";
import { MinusIconOutline } from "@/assets/Icons/MinusIconOutline";
import { PlusIconOutline } from "@/assets/Icons/PlusIconOutline";
import { NavigationLink } from "../NavigationLink/NavigationLink";
import type { MenuItemProps, MenuProps, MenuWithChildrenType } from "./types";
import HTMLRender from "@/shared/globals/UiElements/HTMLRender";
import { theme } from "@/shared/theme";
import { isCurrentRoute } from "@/shared/utils/isCurrentRoute";
import tailwindConfig from "@/../tailwind.config";
import { useParams, usePurePathname } from "@/i18n/i18n-navigation";

function checkIsActive(activePath: string[], pageId: string): boolean {
  return pageId === null ? true : activePath.indexOf(pageId) !== -1;
}

const MobileMenu = ({ items }: { items: MenuWithChildrenType }) => {
  const [activePath, setActivePath] = React.useState<string[]>([]);

  const handleOpenSubMenu = React.useCallback<MenuProps["onOpenSubMenu"]>(
    (parentId, level) => {
      const newPath = [...activePath];
      newPath[level - 1] = parentId!;
      setActivePath(newPath);
    },
    [activePath]
  );

  const handleCloseSubMenu = React.useCallback<(level: number) => void>(
    (level) => {
      setActivePath(activePath.splice(0, level - 1));
    },
    [activePath]
  );

  return (
    <Menu
      parentId={null}
      activePath={activePath}
      onCloseSubMenu={handleCloseSubMenu}
      onOpenSubMenu={handleOpenSubMenu}
      menu={items}
      level={1}
    />
  );
};

const Menu = ({
  activePath,
  parentId,
  onOpenSubMenu,
  onCloseSubMenu,
  menu,
  level,
}: MenuProps) => {
  const isOpen = checkIsActive(activePath, parentId!);

  if (!menu || (level > 1 && !isOpen)) return null;

  return (
    <StyledMenu key={parentId! + level} level={level} isOpen={isOpen}>
      {menu.map((item) => (
        <MenuItem
          activePath={activePath}
          onOpenSubMenu={onOpenSubMenu}
          onCloseSubMenu={onCloseSubMenu}
          isActive={checkIsActive(activePath, item.id)}
          key={item.id}
          level={level}
          item={item}
        />
      ))}
    </StyledMenu>
  );
};

const MenuItem = ({
  item,
  isActive,
  level,
  activePath,
  onOpenSubMenu,
  onCloseSubMenu,
}: MenuItemProps) => {
  const pathname = usePurePathname();
  const params = useParams();
  const isCurrent = isCurrentRoute(pathname, params, item);

  const handleOpenSubMenu = React.useCallback(() => {
    if (isActive) {
      if (onCloseSubMenu) onCloseSubMenu(level);
    } else {
      onOpenSubMenu(item.id, level);
    }
  }, [isActive, item.id, level, onCloseSubMenu, onOpenSubMenu]);

  return (
    <StyledMenuItem isActive={isActive} level={level}>
      <StyledMenuItemContent level={level} isCurrent={isCurrent}>
        {item.type === "Folder" ? (
          <div onClick={handleOpenSubMenu}>
            <NavigationLink {...item.link} type={item?.link?.type!}>
              <NavIconWrapper>
                <HTMLRender html={item?.icon} />
              </NavIconWrapper>
              {item.title}
            </NavigationLink>
          </div>
        ) : (
          <NavigationLink {...item.link} type={item?.link?.type!}>
            <NavIconWrapper>
              <HTMLRender html={item?.icon} />
            </NavIconWrapper>{" "}
            {item.title}
          </NavigationLink>
        )}
        {item?.children?.length !== 0 && (
          <span onClick={handleOpenSubMenu}>
            {isActive ? <MinusIconOutline /> : <PlusIconOutline />}
          </span>
        )}
      </StyledMenuItemContent>
      {item?.children?.length !== 0 && (
        <Menu
          parentId={item.id}
          activePath={activePath}
          menu={item.children!}
          level={level + 1}
          onCloseSubMenu={onCloseSubMenu}
          onOpenSubMenu={onOpenSubMenu}
        />
      )}
    </StyledMenuItem>
  );
};

export default MobileMenu;

/**
 *
 *
 * Styles
 *
 *
 */

const StyledMenuItemContent = styled.div<{
  isCurrent: boolean;
  level: number;
}>`
  display: flex;
  align-items: center;
  font-weight: 500;
  padding: 16px 16px;
  & > a,
  & > a:active,
  & > a:hover,
  & > div {
    flex: 1;
    text-decoration: none;
    color: ${({ theme, isCurrent }) =>
      isCurrent
        ? theme.colors.primary
        : tailwindConfig.theme.extend.colors.gray[700]};
  }
`;

const StyledMenuItem = styled.li<{
  isActive: boolean;
  level: number;
}>`
  cursor: pointer;
  display: flex;
  color: ${({ theme }) => theme.text.default};
  flex-direction: column;
  font-size: 16px;
`;

const StyledMenu = styled.ul<{ isOpen: boolean; level: number }>`
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  width: 100%;
  margin: 0;
  flex-direction: column;
  justify-content: center;
  ${({ theme: { isRTL }, level }) =>
    `${isRTL ? "padding-right" : "padding-left"}: ${(level - 1) * 16}px`}
`;

const NavIconWrapper = styled.div`
  color: ${theme.colors.primary};
`;
